import type { MapGeoJSONFeature } from "maplibre-gl";
import { getCoord } from "@turf/invariant";
import { debugSelection } from "../debug.ts";
import { isPoiSourceLayer, isSemanticSourceLayer } from "./layers.ts";
import type { LngLat } from "./types.ts";

// MapLibre declares queried feature properties as `{ [name: string]: any }`.
// Preserve primitive vector-tile values at this boundary and stringify only
// when producing the name hint consumed by the OSM lookup.
type FeaturePropValue = string | number | boolean;
type FeatureProps = Record<string, FeaturePropValue>;

interface RenderedFeatureLookupTarget {
  lngLat: LngLat;
  nameHint: string | undefined;
}

/**
 * Selects the first usable feature in MapLibre's topmost-first render order.
 * Every candidate is still classified for debug output.
 */
export function selectRenderedFeature(
  features: MapGeoJSONFeature[],
): MapGeoJSONFeature | undefined {
  let selectedFeature: MapGeoJSONFeature | undefined;
  const decisions = features.map((feature) => {
    const props = featureProps(feature);
    const [selectable, reason] = featureSelectability(feature, props);
    const isSelected = selectable && selectedFeature === undefined;

    if (isSelected) selectedFeature = feature;

    return {
      ...featureDebugInfo(feature, props),
      kept: selectable,
      selected: isSelected,
      reason,
    };
  });

  debugSelection("feature query", () => ({
    // MapLibre returns features topmost-first. Preserve that order so the
    // selected feature matches what the user interacted with visually.
    mapLibreFeatures: features,
    selectedFeature,
    decisions,
  }));

  return selectedFeature;
}

export function renderedFeatureLookupTarget(
  feature: MapGeoJSONFeature,
  clickLngLat: LngLat,
): RenderedFeatureLookupTarget {
  const props = featureProps(feature);
  return {
    lngLat: featureAnchorLngLat(feature, clickLngLat),
    nameHint: String(featureName(props) ?? "") || undefined,
  };
}

export function isFeatureSelectable(
  feature: MapGeoJSONFeature,
): boolean {
  return featureSelectability(feature, featureProps(feature))[0];
}

function featureSelectability(
  feature: MapGeoJSONFeature,
  props: FeatureProps,
): [boolean, string] {
  const sourceLayer = feature.sourceLayer;
  if (!sourceLayer) return [false, "missing sourceLayer"];
  if (isSemanticSourceLayer(sourceLayer)) {
    return [true, "semantic POI/label layer"];
  }
  if (!["building", "leisure", "road", "water"].includes(sourceLayer)) {
    return [false, "irrelevant sourceLayer"];
  }
  if (featureName(props)) return [true, "has name"];
  if (props["height"] && Number(props["height"]) > 0) {
    return [true, "has height"];
  }
  if (props["area"] && Number(props["area"]) > 0) {
    return [true, "has area"];
  }
  return [false, "geometry layer without useful properties"];
}

function featureName(props: FeatureProps): FeaturePropValue | undefined {
  return props["name"] ?? props["name:en"] ?? props["ref"];
}

function featureAnchorLngLat(
  feature: MapGeoJSONFeature,
  clickLngLat: LngLat,
): LngLat {
  // Symbol text/icons may be rendered away from their source point. Snapping
  // POIs back to that point makes the nearby OSM lookup accurate. Broad labels
  // and area geometry keep the click, since their anchor may be arbitrary.
  if (
    !feature.sourceLayer ||
    !isPoiSourceLayer(feature.sourceLayer)
  ) {
    return clickLngLat;
  }

  return pointGeometryLngLat(feature.geometry) ?? clickLngLat;
}

function featureDebugInfo(
  feature: MapGeoJSONFeature,
  props: FeatureProps,
): Record<string, unknown> {
  return {
    sourceLayer: feature.sourceLayer,
    featureId: feature.id,
    layerId: feature.layer.id,
    name: featureName(props),
    class: props["class"],
    subclass: props["subclass"],
    height: props["height"],
    area: props["area"],
    ref: props["ref"],
  };
}

function featureProps(feature: MapGeoJSONFeature): FeatureProps {
  return Object.fromEntries(
    Object.entries(feature.properties)
      .filter((entry): entry is [string, FeaturePropValue] =>
        isFeaturePropertyValue(entry[1])
      ),
  );
}

function pointGeometryLngLat(
  geometry: MapGeoJSONFeature["geometry"],
): LngLat | undefined {
  if (geometry.type === "Point") {
    return lngLatFromPosition(getCoord(geometry));
  }

  if (geometry.type === "MultiPoint" && geometry.coordinates.length === 1) {
    return lngLatFromPosition(geometry.coordinates[0]!);
  }

  return undefined;
}

function lngLatFromPosition(
  position: readonly number[],
): LngLat | undefined {
  const lng = position[0];
  const lat = position[1];
  return lng === undefined || lat === undefined ? undefined : [lng, lat];
}

function isFeaturePropertyValue(value: unknown): value is FeaturePropValue {
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean";
}
